import { useState, useEffect, type ComponentType } from 'react'

export type ModalWithConditionalProps = {
  open: boolean
  /*
    onExited callback for conditionally rendered modals.
    Should not be passed directly.
  */
  _onConditionalExited?(): void
}

export function withConditional<T extends ModalWithConditionalProps = ModalWithConditionalProps>(
  WrappedComponent: ComponentType<T>,
) {
  const ConditionalComponent: Settle.FC<Omit<T, '_onConditionalExited'>> = ({ open, children, ...props }) => {
    const [isRendered, setIsRendered] = useState(open)

    useEffect(() => {
      if (open) {
        setIsRendered(true)
      }
    }, [open])

    if (isRendered) {
      return (
        <WrappedComponent
          // "as T" is workaround for https://github.com/Microsoft/TypeScript/issues/28938#issuecomment-450636046
          {...(props as T)}
          open={open}
          _onConditionalExited={() => setIsRendered(false)}
        >
          {children}
        </WrappedComponent>
      )
    }

    return null
  }

  const displayName = WrappedComponent.displayName || WrappedComponent.name || 'Component'

  ConditionalComponent.displayName = `withConditional(${displayName})`
  return ConditionalComponent
}
